import { Typography, Chip, Stack, Link } from '@mui/material';
import { DetailSection, DetailField } from './DetailSection';
import { severityChipSx, stateChipSx } from './severity';
import { CK, statusChipSx } from '../portalStyles';

const fmt = (t) => {
  if (!t) return '';
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

// AWS writes these as SCREAMING_SNAKE enums; the console shows sentence case.
// Unknown values pass through untouched so a new AWS finding type degrades to
// its raw enum instead of vanishing from the panel.
//
// Module-local on purpose. Exporting it would mean the shared findings list —
// which serves CSPM, GuardDuty and Macie too — imports an Inspector-specific
// mapper to prettify one chip, and that chip's raw-enum rendering is the
// existing behaviour for all four engines, not an Inspector regression.
const TYPE_LABELS = {
  PACKAGE_VULNERABILITY: 'Package vulnerability',
  NETWORK_REACHABILITY: 'Network reachability',
  CODE_VULNERABILITY: 'Code vulnerability',
};
const inspectorTypeLabel = (v) => TYPE_LABELS[v] || v || '';

// A partial fix is worth flagging as amber rather than green: it closes some of
// the exposure, not all of it. "No fix available" stays neutral — it is a fact
// about the upstream package, not a misconfiguration the user can act on.
const FIX_INTENT = { YES: 'success', PARTIAL: 'warning', NO: 'neutral' };

// The vulnerability block only makes sense for findings that can carry a score or
// a CVE. NETWORK_REACHABILITY findings carry neither by design, so for those the
// whole section would be a single em dash.
const VULN_TYPES = ['PACKAGE_VULNERABILITY', 'CODE_VULNERABILITY'];

// Amazon Inspector finding body, laid out the AWS-console way: an Overview
// section (type / severity + description and timing), the vulnerability facts
// that make a vulnerability-management screen useful (CVE, Inspector score, fix
// availability), the affected resource, and AWS's remediation guidance.
//
// No status chip here — the drawer's own sticky header already carries one for
// every engine, and a second copy scrolling underneath it is the kind of
// duplicate that starts disagreeing.
export default function InspectorDetailBody({ finding }) {
  if (!finding) return null;

  // Inspector always fills remediation (the engine substitutes generic AWS
  // guidance when AWS supplies none), but guard anyway — this same component
  // renders whatever the drawer passes it.
  const remediation = finding.remediation || {};
  const type = finding.type || '';
  const fixAvailable = finding.fixAvailable || '';

  // 0 means AWS reported NO score, not "scored zero" — NETWORK_REACHABILITY
  // findings legitimately have none. Printing "0.0" would read as a harmless
  // finding, so absence gets an em dash. The CVE row is hidden instead of dashed
  // because only PACKAGE_VULNERABILITY findings ever carry one: an absent score
  // is missing information, an absent CVE is not applicable.
  const score = Number(finding.inspectorScore);
  const scoreLabel = score > 0 ? score.toFixed(1) : '—';

  const showVulnSection = VULN_TYPES.includes(type) || !!finding.cveId || score > 0 || !!fixAvailable;

  return (
    <>
      <DetailSection title="Overview">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
          {type && <Chip size="small" label={inspectorTypeLabel(type)} sx={stateChipSx} />}
          {finding.severity && <Chip size="small" label={finding.severity} sx={severityChipSx(finding.severity)} />}
        </Stack>
        {finding.description && (
          <Typography variant="body2" sx={{ mb: 1.5, color: CK.navy }}>
            {finding.description}
          </Typography>
        )}
        <DetailField label="Finding ID" value={finding.findingId} />
        <DetailField label="Finding type" value={inspectorTypeLabel(type)} />
        <DetailField label="Account" value={finding.accountId} />
        <DetailField label="Region" value={finding.region} />
        <DetailField label="First seen" value={fmt(finding.firstObservedAt)} />
        <DetailField label="Last seen" value={fmt(finding.lastObservedAt)} />
      </DetailSection>

      {showVulnSection && (
        <DetailSection title="Vulnerability">
          <DetailField label="CVE" value={finding.cveId} />
          <DetailField label="Inspector score" value={scoreLabel} />
          {fixAvailable && (
            <DetailField
              label="Fix available"
              value={<Chip size="small" label={fixAvailable} sx={statusChipSx(FIX_INTENT[fixAvailable] || 'neutral')} />}
            />
          )}
        </DetailSection>
      )}

      <DetailSection title="Resource">
        <DetailField label="Resource type" value={finding.resourceType} />
        <DetailField label="Resource" value={finding.resource || finding.resourceId} />
      </DetailSection>

      {(remediation.text || remediation.url) && (
        <DetailSection title="Remediation">
          {remediation.text && (
            <Typography variant="body2" sx={{ mb: 0.5, color: CK.navy, wordBreak: 'break-word' }}>
              {remediation.text}
            </Typography>
          )}
          {remediation.url && (
            <Link href={remediation.url} target="_blank" rel="noreferrer">View documentation</Link>
          )}
        </DetailSection>
      )}
    </>
  );
}
